import { useCallback, useEffect, useMemo, useState } from "react";

import { FIELD_CONFIG, SECTION_LABELS } from "./field-config";
import { OpenaiYamlFieldRenderer } from "./FieldRenderer";
import { OpenaiYamlToolDependenciesEditor } from "./ToolDependenciesEditor";
import { OPENAI_YAML_FORM_SERIALIZE_DEBOUNCE_MS } from "./form-constants";
import { parseOpenaiYaml, serializeOpenaiYaml } from "./yaml-parse";
import type { FieldConfig as FieldConfigType, ToolDependency } from "./types";

const mergeStableToolIds = (
  parsedTools: ToolDependency[],
  prev: ToolDependency[],
): ToolDependency[] =>
  parsedTools.map((tool, i) => (i < prev.length ? { ...tool, _id: prev[i]._id } : tool));

const buildEditorState = (content: string, prevTools: ToolDependency[] = []) => {
  const parsed = parseOpenaiYaml(content);

  return {
    fields: parsed.fields,
    tools: mergeStableToolIds(parsed.tools, prevTools),
  };
};

export interface OpenaiYamlFormEditorProps {
  content: string;
  onChange?: (content: string) => void;
  readOnly?: boolean;
}

export const OpenaiYamlFormEditor = ({
  content,
  onChange,
  readOnly = false,
}: OpenaiYamlFormEditorProps) => {
  const [editorState, setEditorState] = useState(() => buildEditorState(content));
  const { fields, tools } = editorState;

  const groupedFields = useMemo(
    () =>
      FIELD_CONFIG.reduce<Map<string, FieldConfigType[]>>((map, config) => {
        const list = map.get(config.section) ?? [];
        list.push(config);
        map.set(config.section, list);
        return map;
      }, new Map()),
    [],
  );

  useEffect(() => {
    setEditorState(prevState => {
      const serialized = serializeOpenaiYaml(prevState.fields, prevState.tools);
      if (serialized === content) {
        return prevState;
      }

      return buildEditorState(content, prevState.tools);
    });
  }, [content]);

  useEffect(() => {
    if (readOnly || !onChange) {
      return undefined;
    }

    const serialized = serializeOpenaiYaml(fields, tools);
    if (serialized === content) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      onChange(serialized);
    }, OPENAI_YAML_FORM_SERIALIZE_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [content, fields, onChange, readOnly, tools]);

  const updateField = useCallback((yamlKey: string, value: string | boolean) => {
    setEditorState(prevState => ({
      ...prevState,
      fields: { ...prevState.fields, [yamlKey]: value },
    }));
  }, []);

  const updateTools = useCallback((nextTools: ToolDependency[]) => {
    setEditorState(prevState => ({
      ...prevState,
      tools: nextTools,
    }));
  }, []);

  return (
    <div className="mx-auto h-full min-h-0 w-full max-w-5xl overflow-y-auto px-4 py-6">
      <div className="space-y-6">
        {Array.from(groupedFields.entries()).map(([section, sectionFields]) => (
          <fieldset key={section}>
            <legend className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {SECTION_LABELS[section] ?? section}
            </legend>
            <div className="ml-3 grid gap-3">
              {sectionFields.map(config => (
                <OpenaiYamlFieldRenderer
                  key={config.yamlKey}
                  config={config}
                  value={fields[config.yamlKey]}
                  readOnly={readOnly}
                  onChange={val => updateField(config.yamlKey, val)}
                />
              ))}
            </div>
          </fieldset>
        ))}

        <fieldset>
          <legend className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Dependencies
          </legend>
          <OpenaiYamlToolDependenciesEditor tools={tools} onChange={updateTools} readOnly={readOnly} />
        </fieldset>
      </div>
    </div>
  );
};
